// Common types used throughout the application
import { Node, Edge } from 'reactflow'; // Import Node and Edge types
import type { Profile } from './profiles';

export * from './profiles';

/** Platform user roles (enforced by backend). Use UserRoles enum for values in code. */
export type UserRole = 'super_admin' | 'admin' | 'user' | 'demo';

/** Enum of platform roles — use these instead of hardcoded strings. */
export const UserRoles = {
  SuperAdmin: 'super_admin',
  Admin: 'admin',
  User: 'user',
  Demo: 'demo',
} as const;

/** Type-safe array of all user roles (e.g. for dropdowns). */
export const USER_ROLES: readonly UserRole[] = [
  UserRoles.User,
  UserRoles.Admin,
  UserRoles.SuperAdmin,
  UserRoles.Demo,
];

export interface Organization {
  id: string;
  name: string;
  description?: string | null;
  domain?: string | null;
  sector_template_id?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
  forge_client_id?: string | null;  // ID of the linked Forge client
  is_demo?: boolean;
  is_demo_template?: boolean;
  /** The empty "GenieFactory" management org super admins live in by default. */
  is_platform?: boolean;
}

/** Per-organization white-label branding. Any field null = GenieFactory default. */
export type BrandingTheme = 'light' | 'dark';

export interface OrganizationBranding {
  logo_url?: string | null;        // proxy image path served from the API
  primary_color?: string | null;   // hex, e.g. "#2563eb" (Accent principal)
  secondary_color?: string | null; // hex (Accent secondaire)
  default_theme?: BrandingTheme | null;
  font_family?: string | null;     // curated Google Font name
}

/** Editable branding fields (logo handled by the dedicated upload endpoint). */
export interface OrganizationBrandingUpdate {
  primary_color?: string | null;
  secondary_color?: string | null;
  default_theme?: BrandingTheme | null;
  font_family?: string | null;
}

/** Organization feature modules (mirrors backend app/core/modules.py). */
export const ORG_MODULES = [
  'domains',
  'usecases',
  'cadrages',
  'conversations',
  'chatbots',
  'knowledge-base',
  'evaluations',
  'genie-artifact',
  'cowork',
  'projects',
  'gallery',
  'portfolio',
] as const;
export type OrgModule = (typeof ORG_MODULES)[number];

/** Existing modules are on by default; the Olisé ones are off. */
export const DEFAULT_ORG_MODULES: Record<OrgModule, boolean> = {
  domains: true,
  usecases: true,
  cadrages: true,
  conversations: true,
  chatbots: true,
  'knowledge-base': true,
  evaluations: true,
  'genie-artifact': true,
  cowork: true,
  projects: false,
  gallery: false,
  portfolio: false,
};

export type OrgExperience = 'etabli' | 'olise';
export type OrgThemePreset = 'default' | 'graphite' | 'nebula' | 'argile' | 'acier' | 'sauge';
export type OrgLanding = '/welcome' | '/projets' | '/portefeuille';

/** Effective experience & module settings of an organization. */
export interface OrganizationUiSettings {
  experience: OrgExperience;
  modules: Record<OrgModule, boolean>;
  app_name?: string | null;
  tagline?: string | null;
  sector?: string | null;
  theme_preset: OrgThemePreset;
  landing?: OrgLanding | null;
  knowledge_private_by_default: boolean;
  /** Written by God only. */
  analysis_limits?: Record<string, number> | null;
}

export type OrganizationUiSettingsUpdate = Partial<
  Omit<OrganizationUiSettings, 'modules'> & { modules: Partial<Record<OrgModule, boolean>> }
>;

// Invitation types
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface Invitation {
  id: string;
  email: string;
  organization_id: string;
  invited_by: string;
  status: InvitationStatus;
  expires_at: string;
  created_at: string;
  accepted_at?: string | null;
}

export interface InvitationWithDetails extends Invitation {
  organization_name: string;
  inviter_name?: string | null;
}

export interface InvitationCreate {
  email: string;
  organization_id: string;
}

export interface InvitationValidationResponse {
  valid: boolean;
  email?: string;
  organization_name?: string;
  organization_id?: string;
  expires_at?: string;
  logto_verification_code?: string;  // LogTo verification code for simplified signup
  message?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  // URL d'avatar complet (fourni par le backend, ex: proxy MinIO)
  avatar_url?: string | null;
  // Ancien champ éventuel pour compatibilité locale
  avatar?: string;
  organization_id?: string | null;
  organization_is_active?: boolean | null;
  organization_is_demo?: boolean | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string | null;
  organization?: Organization;
  domains?: Domain[];
  // Logto integration
  logto_id?: string | null;
  // User preferences
  preferences?: Record<string, any> | null;
  // Soft-delete fields
  is_deleted?: boolean;
  deleted_at?: string | null;
  restored_at?: string | null;
  // Profile (feature-level RBAC)
  profile_id?: string | null;
  profile?: Profile | null;
  /** /users/me: the profile in force (assigned, else org/role default); its ui_config drives the UI. */
  effective_profile?: Profile | null;
}

export interface DeletedUser extends User {
  deleted_at: string;
  deleted_by_id?: string | null;
  deleted_by_name?: string | null;
  content_transferred_to_id?: string | null;
  content_transferred_to_name?: string | null;
}

export interface UserDeletionLog {
  id: string;
  deleted_user_id: string;
  deleted_user_email: string;
  deleted_user_name?: string | null;
  deleted_by_id?: string | null;
  deleted_by_name?: string | null;
  content_transferred_to_id?: string | null;
  content_transferred_to_name?: string | null;
  deleted_at: string;
  log: string;
}

export interface Persona {
  id: string;
  name: string;
  role: string;
  description?: string | null; // Ajouter description basé sur la réponse API
  // URL presignée renvoyée par le backend pour l'avatar (si existant)
  avatar_url?: string | null;
  citation?: string | null; // Utiliser le nom de l'API
  objectif?: string | null; // Utiliser le nom de l'API (supposer string simple pour l'instant)
  frustration?: string | null; // Utiliser le nom de l'API (supposer string simple pour l'instant)
  // Ajouter d'autres champs si pertinent
}

export interface Project {
  id: string;
  name: string;
  description: string;
  client: string;
  scope: string;
  team: User[];
  startDate: string;
  endDate: string;
  createdAt: string;
  // Ajout des nouveaux statuts et du type de mission
  status: 'active' | 'completed' | 'draft' | 'planned' | 'in_progress';
  type: 'cadrage';
  companyInfo: CompanyInfo;
  // Rendre plan optionnel pour la compatibilité initiale
  plan?: InterviewPlanDepartment[];
  // Fields for Cadrage description
  rationale?: string; // Pourquoi / Comment
  technicalElements?: string; // Éléments Techniques
  guidedInterviews?: Interview[]; // Utiliser le type Interview unifié
  documentTreeRootId?: string; // ID racine de l'arborescence documentaire associée
  questionnaires?: QuestionnaireLink[]; // Ajout pour les questionnaires liés
  maturityData?: MaturityRadarData | null; // Ajout des données de maturité
  useCases?: UseCaseSummary[]; // Ajout pour la liste des cas d'usage
  useCasesDetail?: UseCaseDetail[]; // Ajout pour les détails des cas d'usage
  // sourceAuditId removed (no audit notion)
  sourceUseCaseId?: string; // Optional link to the specific use case within the source audit
  architectureNotesMd?: string; // Markdown content for architecture notes
  architectureDiagramMd?: string; // Mermaid syntax for architecture diagram
  databaseSchemaNotesMd?: string; // Markdown content for database schema notes
  databaseSchemaDiagramMd?: string; // Mermaid syntax for database schema diagram
}

export interface CompanyInfo {
  name: string;
  sector: string;
  size: string;
  revenue: string;
  location: string;
  mainContact: {
    name: string;
    role: string;
    email: string;
    phone: string;
  };
}

// Types related to Interview Plans (as defined initially in AuditDetailPage)
export interface TemplateListItem {
  id: string;
  name: string;
  template_type: 'cadrage' | 'other';
}

export interface InterviewPlanItem { // Renamed from InterviewPlanItemForm for clarity, used in Read model
  id: string; // ID of the plan item itself
  interview_template_id: string;
  planned_interviewee: string;
  order: number;
  // Add other fields returned by API if any (e.g., linked interview status)
}

// Type for the form data when creating/editing plan items
export interface InterviewPlanItemForm {
  id?: string; // Optional: temporary client-side ID or existing ID for update logic
  interview_template_id: string;
  planned_interviewee: string;
  order: number;
}

// Represents the structure of an Interview Plan as read from the API
export interface InterviewPlanRead {
  id: string; // ID of the plan
  name: string;
  description?: string | null;
  domain_id?: string | null; // ID of the domain (if linked to a domain)
  cadrage_id?: string | null; // ID of the cadrage (if linked to a cadrage)
  owner_id: string;
  created_at: string;
  updated_at?: string | null;
  items: InterviewPlanItem[]; // Use the type with the guaranteed 'id'
}
// Removed erroneous lines from previous diff application
// Nouveaux types pour le plan d'entretiens
export interface InterviewPlanType {
  type: string; // Ex: 'Métier', 'Technique'
  count: number;
  templateId: string; // ID du template à utiliser pour ce type
}
export interface InterviewPlanDepartment {
  id: string; // Ex: 'dept-dsi'
  name: string; // Ex: 'DSI'
  types: InterviewPlanType[];
}
// Fin des nouveaux types pour le plan d'entretiens

export interface MaturityScore {
  technology: number;
  data: number;
  skills: number;
  process: number;
  change: number;
}

export interface UseCase {
  id: string;
  title: string;
  description: string;
  team: string;
  department: string;
  opex: number;
  capex: number;
  teamPreparation: number;
  compliance: number;
  reliability: number;
  ux: number;
  scalability: number;
  businessValue: number;
  roi: number;
  complexity: number;
  category: string;
  priority: 'quick-win' | 'strategic' | 'tactical' | 'questioning';
}

// Define a specific type for Use Case Status
export type UseCaseStatus = 'Identified' | 'Qualified' | 'Prioritized' | 'Abandoned' | 'In Progress' | 'Completed';

// Summary for Use Case listing
export interface UseCaseSummary {
  id: string;
  name: string;
  short_description: string | null; // Match API response (snake_case, nullable)
  value_score?: number | null; // Global business impact/score (nullable)
  complexity_score?: number | null; // Global complexity/effort score (nullable)
  status: UseCaseStatus; // Use the dedicated type
  // Linking / segmentation fields
  // audit_id removed (no audit notion)
  domain_id?: string | null; // Link to domain if it exists
  cadrage_id?: string | null; // Linked cadrage when available
  has_linked_cadrage?: boolean; // UI helper computed from cadrages list when needed
  category?: string | null; // High-level category for dashboard filters (ex: Automatisation, Humain)
  forge_application_id?: string | null; // ID of the linked Forge application
}

export interface EvaluationItem {
  dimension: string;
  score: number; // Assuming numeric score based on context, adjust if needed
  comment: string;
}

export interface Analysis {
  strengths: string[];
  weaknesses: string[];
}

// Detail for a specific Use Case
export interface UseCaseDetail extends UseCaseSummary {
  // Inherited: id, name, short_description, status, valueScore?, complexityScore?
  description: string | null; // Match API (nullable)
  synthese: string | null; // Match API (nullable) — executive synthesis
  description_metier: string | null; // Match API (snake_case, nullable) — business description
  business_need: string | null; // Match API (snake_case, nullable)
  input: string | null; // Match API (nullable)
  output: string | null; // Match API (nullable)
  technical_elements: string | null; // Match API (snake_case, nullable)
  evaluation_data: EvaluationItem[] | null; // Match API (snake_case, nullable) - Assuming EvaluationItem structure is correct
  analysis_data: Analysis | null; // Match API (snake_case, nullable) - Assuming Analysis structure is correct
  recommendation: string | null; // Match API (nullable)
  // audit_id removed (no audit notion)
  cadrage_id: string | null; // Match API (snake_case, nullable)
  involved_tools: string[] | null; // Match API (snake_case, nullable)
  personas?: Persona[]; // Already present, assuming structure matches API
  comments?: Comment[]; // Add comments from API response (type Comment exists)
  react_flow_data?: any | null; // Add react_flow_data from API response (use 'any' for now)
  // Add other fields from API response if needed (e.g., created_at, updated_at)
  created_at?: string;
  updated_at?: string;
  // id is inherited from UseCaseSummary
}

export interface Note {
  id: string;
  projectId: string; // Conservé pour potentielle utilisation future
  userId: string; // Conservé pour potentielle utilisation future
  title: string; // Conservé pour potentielle utilisation future
  content: string; // Contenu Markdown
  tags: string[]; // Conservé pour potentielle utilisation future
  type?: 'interview' | 'meeting' | 'observation' | 'general'; // Rendu optionnel
  createdAt: string;
  updatedAt: string;
  assignedTo?: { type: 'mission' | 'interview'; id: string }; // Nouveau champ pour l'assignation
}

// Nouvelle définition pour Document (basée sur le schéma backend Document)
export interface Document {
  id: string; // UUID
  name: string;
  folderPath: string; // Logical path
  mimeType: string;
  size: number; // in bytes
  minioPath: string; // Full path in Minio
  ownerId: string; // UUID
  auditId: string | null; // UUID
  cadrageId: string | null; // UUID
  createdAt: string; // ISO datetime string
  updatedAt: string | null; // ISO datetime string
}

// Définition pour PresignedUrl (basée sur le schéma backend PresignedUrl)
export interface PresignedUrl {
  url: string;
}

export interface Folder {
  id: string;
  name: string;
  parentId: string | null; // ID of the parent folder (null for root)
  childFolders: Folder[];
  childDocuments: Document[];
}

// Nouvelle définition pour InterviewAnswer (basée sur InterviewAnswerRead du backend)
export interface InterviewAnswer {
  id: string; // UUID
  interview_id: string; // UUID - match backend snake_case
  question_order: number | string; // Backend stores as string but we can handle both
  question_text: string;
  answer_text: string | null;
}

// Nouvelle définition pour Interview (basée sur InterviewRead du backend)
export interface Interview {
  id: string; // UUID
  ownerId: string; // UUID
  name: string | null;
  status: string | null; // 'Planned', 'In Progress', 'Completed' etc.
  interview_date: string | null; // Correction: camelCase -> snake_case
  interviewee_name: string | null; // Correction: camelCase -> snake_case
  general_notes: string | null; // Correction: camelCase -> snake_case
  templateId: string; // UUID
  auditId: string | null; // UUID
  cadrageId: string | null; // UUID
  // 'answers' should be an array of InterviewAnswer objects, matching the backend structure
  answers: InterviewAnswer[];
  // Add fields to align more closely with InterviewSession and usage
  freeNotes?: string | null; // Make freeNotes optional to match potential backend schema variations
  createdAt: string; // ISO 8601 date string (Assuming non-optional from backend)
  updatedAt: string; // ISO 8601 date string (Assuming non-optional from backend)
  // relatedTo audit variant removed
  // Optional expanded relationships (can be added if needed by frontend)
  // owner?: User;
  // template?: InterviewTemplate; // Assuming InterviewTemplate type exists/will be defined
}

export interface QuestionnaireResponse {
  id: string;
  projectId: string;
  userId: string;
  responses: {
    [questionId: string]: string | number | boolean;
  };
  completedAt: string;
}

/** Backend template_type from API (snake_case). Used only for API request/response typing. */
export type BackendTemplateType = 'audit' | 'cadrage';

// Interface pour les questions dans le contexte du formulaire de Template
export interface TemplateQuestionInForm {
  id: string; // ID généré côté client pour la gestion des clés React
  text: string;
  order: number; // Rendu REQUIS pour la cohérence avec le formulaire
}

export interface Template {
  id: string;
  name: string;
  description?: string; // Optionnel
  questions?: TemplateQuestionInForm[]; // Utilise la nouvelle interface pour les questions
  createdAt?: string; // Rendre optionnel car pas toujours présent à la création
  updatedAt?: string; // Rendre optionnel
  /** False when the template is used by interviews or plan items (cannot be deleted) */
  can_delete?: boolean;
}


// Ajout des types pour la Notation
export interface ScoringLevel {
  name: string;
  value: number | string; // Peut être numérique ou qualitatif
  description?: string;
}

export interface ScoringScale {
  id: string;
  name: string;
  levels: ScoringLevel[];
}

export type CriterionType = 'numeric' | 'qualitative';

export interface ScoringCriterion {
  id: string;
  name: string;
  type: CriterionType;
  scale?: ScoringLevel[]; // Pour qualitatif (ex: Faible, Moyen, Élevé)
  range?: { min: number; max: number }; // Pour numérique (ex: 1-5)
  weight?: number; // Optionnel
  description?: string; // Ajout de la description optionnelle
}

export interface UseCaseScore {
  useCaseId: string; // Lien vers le cas d'usage
  scores: {
    [criterionId: string]: number | string; // Stocke la valeur du score pour chaque critère
  };
  overallScore?: number; // Score global calculé (optionnel)
  feasibility?: string; // Exemple spécifique demandé
}

// Ajout des types pour les Paramètres
export interface UserProfileSettings {
  id: string;
  name: string;
  email: string;
  role: UserRole; // Réutilise le type UserRole défini plus haut
  // Le changement de mot de passe est généralement une action, pas une donnée stockée ici
}

export interface PreferencesSettings {
  userId: string;
  theme: 'light' | 'dark';
  language: 'fr' | 'en'; // Exemple de langues
  notifications: {
    email: boolean;
    inApp: boolean;
  };
}

export interface Permission {
  id: string;
  action: string; // ex: 'create_mission', 'edit_template', 'manage_users'
  resource: string; // ex: 'mission', 'template', 'user', '*' (pour global)
}

export interface RolePermission {
  role: UserRole;
  permissions: Permission[];
  description?: string;
}

export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
  category?: string; // Optionnel
}

// Types for Guided Interviews

// Structure d'une question dans un template (basée sur QuestionSchema)
export interface Question {
  text: string;
  order: number;
}

// Structure d'un template d'entretien (basée sur InterviewTemplateRead)
export interface InterviewTemplate {
  id: string; // UUID
  ownerId: string; // UUID
  name: string;
  description: string | null;
  templateType?: BackendTemplateType;
  /** API response often uses snake_case */
  template_type?: BackendTemplateType;
  questions: Question[];
  // owner?: User; // Optional relation
}

// Supprimer l'interface InterviewSession car elle est redondante et incohérente avec Interview
// export interface InterviewSession { ... }

// Interface for the new interview modal form data
export interface NewInterviewData {
  title: string;
  departmentId: string;
  planType: string; // 'Métier' | 'Technique'
  templateId: string;
}
// Interface for Questionnaire links within a Mission
export interface QuestionnaireLink {
  id: string;
  title: string;
  status: 'Not Started' | 'In Progress' | 'Completed';
  link?: string; // Optional link to the questionnaire itself
}

// Ajout du type pour les données du radar de maturité
export interface MaturityRadarDataset {
  label: string;
  data: number[];
  backgroundColor?: string; // Optionnel: couleur de fond
  borderColor?: string; // Optionnel: couleur de la bordure
  borderWidth?: number; // Optionnel: épaisseur de la bordure
  fill?: boolean; // Optionnel: remplir la zone
}

export interface MaturityRadarData {
  labels: string[];
  datasets: MaturityRadarDataset[];
}

// Type for Technological Blockers
export interface Blocker {
  id: string; // UUID unique
  missionId: string; // ID de la mission (Cadrage) associée
  description: string; // Description du verrou
  criticite: 'faible' | 'moyenne' | 'haute' | null; // Criticité
  strategieMitigation: string | null; // Stratégie
}

// Types for Use Case Qualification Feature (Backend API & Frontend)

// export type MoSCoWPriority = 'Must Have' | 'Should Have' | 'Could Have' | 'Won\'t Have'; // Defined in backend schema as string

// Base properties for creating/updating a qualification record
export interface QualificationBase {
  value_score?: number | null;
  complexity_score?: number | null;
  moscow?: string | null; // e.g., 'MUST', 'SHOULD', 'COULD', 'WONT'
  rice_reach?: number | null;
  rice_impact?: number | null;
  rice_confidence?: number | null;
  rice_effort?: number | null;
  notes?: string | null;
  needs_review?: boolean;
  review_type?: string | null; // 'Legal', 'Tech', 'Other'
  review_other_name?: string | null; // Custom name if review_type is 'Other'
}

// Schema for creating a new qualification entry via API
export interface QualificationCreate extends QualificationBase { }

// Schema representing a qualification record returned by the API
export interface Qualification extends QualificationBase {
  id: string; // UUID
  use_case_id: string; // UUID
  timestamp: string; // ISO DateTime string
  author_id: string; // UUID
  author?: User | null; // Optional author details (populated from backend)
  editor_id?: string | null; // UUID of the last editor
  editor?: User | null; // Optional editor details (populated from backend)
}


// Types for React Flow data persistence
export interface ReactFlowDataObject {
  nodes: Node[];
  edges: Edge[];
  viewport?: any; // Viewport can be complex, using 'any' for simplicity
}

export interface ReactFlowData extends ReactFlowDataObject {
  id: string; // UUID for the specific diagram data
  use_case_id: string; // Link to the use case
  created_at: string;
  updated_at: string;
}


// Types for Comments
export interface CommentOwner {
  id: string;
  email: string;
  name?: string | null;
  avatar_url?: string | null;
}
export interface Comment {
  id: string;
  use_case_id: string;
  content: string;
  owner_id: string;
  owner?: CommentOwner; // Optional: populated by backend
  created_at: string;
  updated_at: string;
}

export interface CommentCreate {
  content: string;
}

export interface CommentUpdate {
  content: string;
}

// Types for User creation (simplified, adjust as per backend schema)
export interface UserCreate {
  email: string;
  password?: string; // Password might not be part of UserRead but needed for create
  // Add other fields like name, role if they are part of creation
}


// Types for Minio/Document Management
export interface MinioItem {
  name: string;
  type: "folder" | "file";
  /**
   * Optional: when this item corresponds to a persisted Document record,
   * we can attach its database identifier to enable actions like
   * deletion or AI-based import.
   */
  documentId?: string;
  /**
   * Optional MinIO object path for the file, when available from the API.
   */
  minioPath?: string;
  /** Optional MIME type for files (e.g. for preview). */
  mimeType?: string;
  // Add other relevant fields like size, lastModified if provided by API
}
export interface FolderListing {
  prefix: string;
  subfolders: MinioItem[];
  files: MinioItem[];
}


// Types for Interview CRUD operations (based on backend schemas)

// For updating an answer within an interview
export interface InterviewAnswerUpdate {
  id: string; // UUID to uniquely identify the answer
  question_order: number;
  answer_text?: string | null;
}

// For updating an interview
export interface InterviewUpdate {
  name?: string | null;
  status?: string | null;
  interview_date?: string | null; // ISO string or Date
  interviewee_name?: string | null;
  general_notes?: string | null;
  answers?: InterviewAnswerUpdate[]; // Array of answer updates
}

// For creating an interview
export interface InterviewCreate {
  name?: string | null;
  status?: string | null;
  interview_date?: string | null; // ISO string or Date
  interviewee_name?: string | null;
  general_notes?: string | null;
  template_id: string; // UUID of the InterviewTemplate
  domain_id: string; // UUID of the domain (required)
  cadrage_id?: string | null; // UUID if linked to a cadrage (optional)
  answers?: Array<{ question_order: number; question_text: string; answer_text?: string | null }>; // Initial answers (optional)
}


// Types for Use Case CRUD (based on backend schemas)
export interface UseCaseCreate {
  name: string;
  domain_id: string; // UUID of the domain (required)
  short_description?: string | null;
  description?: string | null;
  synthese?: string | null;
  description_metier?: string | null;
  business_need?: string | null;
  input?: string | null;
  output?: string | null;
  technical_elements?: string | null;
  involved_tools?: string[] | null;
  cadrage_id?: string | null; // Link to a cadrage
  category?: string | null; // High-level category (optional, used in dashboard filters)
  status?: string; // Status of the use case
  value_score?: number | null; // Business value score (1-10)
  complexity_score?: number | null; // Technical complexity score (1-10)
}

export interface UseCaseUpdate {
  name?: string;
  status?: UseCaseStatus;
  short_description?: string | null;
  description?: string | null;
  synthese?: string | null;
  description_metier?: string | null;
  business_need?: string | null;
  input?: string | null;
  output?: string | null;
  technical_elements?: string | null;
  involved_tools?: string[] | null;
  category?: string | null; // Keep category updatable if backend supports it
  value_score?: number | null;
  complexity_score?: number | null;
  // cadrage_id is usually set at creation or via specific linking logic
  forge_application_id?: string | null; // ID of the linked Forge application
}


// Types for Cadrage CRUD (based on backend schemas)

// Base for Cadrage (common fields for create/update/read)
export interface CadrageBase {
  name: string;
  type: string; // Required field from backend
  description?: string | null;
  synthese?: string | null;
  content?: string | null;
  client?: string | null; // General info field
  status?: 'draft' | 'validated' | 'planned' | 'progress' | 'forge' | 'deployed' | null; // Cadrage lifecycle status
  use_case_id: string; // Required field from backend
  architecture_notes?: string | null;
  db_schema_notes?: string | null;
  mermaid_schema?: string | null;
  company_name?: string | null;
  company_sector?: string | null;
  company_size?: string | null;
  company_revenue?: string | null;
  company_location?: string | null;
  main_contact_name?: string | null;
  main_contact_role?: string | null;
  main_contact_email?: string | null;
  main_contact_phone?: string | null;
  forge_link?: string | null; // Lien frontend vers la Forge
}

// For creating a Cadrage
export interface CadrageCreate extends CadrageBase { }

// For updating a Cadrage
export interface CadrageUpdate extends Partial<CadrageBase> { }


// For reading a Cadrage (includes ID and timestamps)
export interface CadrageRead extends CadrageBase {
  id: string; // UUID
  created_at: string; // ISO datetime string
  updated_at: string | null; // ISO datetime string
  blockers?: BlockerRead[];
  version: number; // Version number for cadrage versioning
  is_active: boolean; // Whether this version is active
  artifact_session_id?: string | null; // Set when this cadrage was promoted from a Genie Artifact session
}

// Types for Blocker CRUD (based on backend schemas)
export interface BlockerBase {
  description: string;
  criticite?: 'faible' | 'moyenne' | 'haute' | null;
  strategie_mitigation?: string | null;
  cadrage_id: string; // UUID of the parent cadrage
}

// For creating a Blocker
export interface BlockerCreate extends BlockerBase { }

// For updating a Blocker
export interface BlockerUpdate extends Partial<Omit<BlockerBase, 'cadrage_id'>> { } // cadrage_id usually not updatable

// For reading a Blocker
export interface BlockerRead extends BlockerBase {
  id: string; // UUID
  // created_at, updated_at if available from backend
}


// Extended Cadrage types for more detailed views or specific operations

// Cadrage as stored in DB (might include internal fields or relationships)
export interface CadrageInDB extends CadrageBase {
  id: string; // UUID
  owner_id: string; // UUID of the user who created it
  // Timestamps are usually present
  created_at: string;
  updated_at: string | null;
}

// CadrageRead with team members populated (currently same as CadrageRead)
export interface CadrageReadWithTeam extends CadrageRead {
  // Note: team members functionality not implemented yet
  members?: User[]; // Optional: when available, list of team members for the cadrage
}


// Frontend specific type for Blocker form data
export interface BlockerFormData {
  description: string;
  criticite: 'faible' | 'moyenne' | 'haute' | ''; // Allow empty for initial state
  strategieMitigation: string;
}
// Types for creating and updating Interview Templates via API (single payload shape; use Partial for update)
export interface InterviewTemplateQuestionCreate {
  text: string;
  order: number;
}

export interface InterviewTemplatePayload {
  name: string;
  description?: string | null;
  template_type: BackendTemplateType;
  questions: InterviewTemplateQuestionCreate[];
}

// Types for Organization CRUD operations
export interface OrganizationCreate {
  name: string;
  description?: string | null;
  domain?: string | null;
  sector_template_id: string;
  is_active?: boolean;
}

export interface OrganizationUpdate {
  name?: string;
  description?: string | null;
  domain?: string | null;
  sector_template_id?: string | null;
  is_active?: boolean;
  forge_client_id?: string | null;
}

export interface OrganizationWithUsers extends Organization {
  users: User[];
}

// Super-admin Users page (admin user management)
export interface AdminUserStats {
  total_users: number;
  total_organizations: number;
  users_in_logto: number;
  users_not_in_logto: number;
  logto_not_in_db: number;
}

export interface UserWithLogtoStatus extends User {
  logto_status: 'active' | 'not_in_logto';
  organization_name?: string | null;
  last_sign_in_at?: string | null;
}

export interface LogtoUserStub {
  id: string;
  primary_email?: string | null;
  name?: string | null;
  username?: string | null;
}

export interface NonSyncResponse {
  in_db_not_in_logto: User[];
  in_logto_not_in_db: LogtoUserStub[];
}

export interface AdminUserListResponse {
  items: UserWithLogtoStatus[];
  total: number;
}

/** Unified row for AdminUsersTable: DB user, Logto-only stub, or both */
export type AdminUserRowSource = 'db' | 'logto' | 'db_and_logto';

export interface AdminUserRow {
  id: string;
  email: string;
  name?: string | null;
  created_at?: string | null;
  last_sign_in_at?: string | null;
  logto_status?: 'active' | 'not_in_logto';
  organization_name?: string | null;
  organization_id?: string | null;
  logto_id?: string | null;
  source: AdminUserRowSource;
  is_active?: boolean;
  role?: string | null;
  profile_id?: string | null;
  profile_key?: string | null;
  profile_name?: string | null;
}

// LLM token usage (super_admin dashboard)
export type LLMUsageTask =
  | 'import_doc'
  | 'generate_report'
  | 'chat'
  | 'fill_form'
  | 'rag_chat'
  | 'rag_embedding'
  | 'indexing_embedding'
  | 'ontology_extraction'
  | 'starter_questions'
  | 'unknown';

export interface LLMUsageStats {
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  by_task?: Record<string, number>;
  by_provider?: Record<string, number>;
  by_model?: Record<string, number>;
}

export interface LLMUsageLogEntry {
  id: string;
  created_at: string;
  provider: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  task: string;
  user_id?: string | null;
  organization_id?: string | null;
  user_email?: string | null;
  user_name?: string | null;
  organization_name?: string | null;
  extra?: Record<string, unknown> | null;
}

export interface LLMUsageLogsResponse {
  items: LLMUsageLogEntry[];
  total: number;
}

// Domain types
export interface Domain {
  id: string;
  name: string;
  description?: string | null;
  organization_id: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
  organization?: Organization;
  forge_id?: string | null;  // ID of the linked Forge
}

export interface DomainWithDetails extends Domain {
  members: DomainMember[];
  cadrages: CadrageRead[];
  total_members?: number;
  total_cadrages?: number;
  total_use_cases?: number;
}

export interface DomainMember {
  user_id: string;
  role: 'admin' | 'member' | 'viewer';
  user?: User;
  joined_at?: string;
}

export interface DomainStats {
  total_members: number;
  total_cadrages: number;
  total_use_cases: number;
  total_documents: number;
  active_cadrages: number;
  completed_cadrages: number;
}

export interface DomainCreate {
  name: string;
  description?: string | null;
  organization_id: string;
  member_ids?: string[];
}

// API request type for creating domain (organization_id is set by backend)
export interface DomainCreateAPI {
  name: string;
  description?: string | null;
  member_ids?: string[];
}

export interface DomainUpdate {
  name?: string;
  description?: string | null;
  is_active?: boolean;
  forge_id?: string | null;
}

// AI Helper types
export interface FormField {
  name: string;
  description: string;
  field_type?: string;
  required?: boolean;
  current_value?: string | number | boolean | null;
}

export interface FieldValue {
  name: string;
  value: string | number | boolean | null;
  confidence: number;
  reasoning: string;
}

export interface AIFillerRequest {
  user_text: string;
  fields: FormField[];
  form_context?: string;
}

export interface AIFillerResponse {
  success: boolean;
  filled_fields: FieldValue[];
  message: string;
  suggestions: string[];
}

// AI Report types
export interface AIReportRequest {
  entity_type: 'use_case' | 'cadrage';
  entity_id: string;
}

export interface AIReportResponse {
  success: boolean;
  report_content: string;
  entity_type: string;
  entity_name: string;
  message: string;
}

// =============================================================================
// Forge API Types
// These represent data fetched from the external Forge API (not stored locally)
// Based on the Forge API OpenAPI spec
// =============================================================================

// Resource quotas for a client
export interface ResourceQuotas {
  cpu: string;
  memory: string;
  pods: string;
}

// Forge Client (maps to Organization in Etabli)
export interface ForgeClient {
  client_id: string;  // Primary ID from Forge API
  user_id?: string | null;
  name: string;
  quotas?: ResourceQuotas | null;
  forge_ids: string[];
  created_at?: string;
  updated_at?: string;
  // Alias for frontend compatibility
  id?: string;  // Will be set to client_id
}

// Pod status in a forge
export interface PodStatus {
  name: string;
  phase: string;
  ready: boolean;
  restarts: number;
}

// Forge (maps to Domain in Etabli)
export interface Forge {
  forge_id: string;  // Primary ID from Forge API
  client_id: string;
  name: string;
  display_name: string;
  status: string;  // creating, deploying, deployed, error, updating, deleting
  system_apps: string[];
  custom_apps: string[];
  forge_urls: Record<string, string>;
  api_endpoints: Record<string, string>;
  argocd_pods_status: PodStatus[];
  created_at?: string;
  updated_at?: string;
  // Alias for frontend compatibility
  id?: string;  // Will be set to forge_id
  description?: string | null;  // Will be set to display_name
}

export interface ForgeWithStats extends Forge {
  resource_limits: Record<string, string>;
  resource_usage?: Record<string, string> | null;
  applications_count: number;
  running_applications: number;
  stopped_applications: number;
}

export interface ForgeWithApplications extends Forge {
  applications: ForgeApplication[];
}

// Forge Application / Custom App (maps to UseCase in Etabli)
export interface ForgeApplication {
  app_id: string;  // Primary ID from Forge API
  forge_id: string;
  client_id: string;
  name: string;
  app_type: string;  // default_app, ecommerce, dashboard, api_custom, frontend_react
  status: string;  // creating, initializing, building, build_failed, deploying, deployed, error, updating, stopped
  template_name: string;
  source_repo_url: string;
  urls: Record<string, string>;
  last_build_at?: string | null;
  health_status: string;  // healthy, unhealthy, unknown
  created_by: string;
  created_at?: string;
  updated_at?: string;
  // Aliases for frontend compatibility
  id?: string;  // Will be set to app_id
  state?: string | null;  // Derived from status
  frontend_url?: string | null;  // From urls
  backend_url?: string | null;  // From urls
  repo_url?: string | null;  // From urls
  description?: string | null;
}

export interface BuildInfo {
  build_id: string;
  batch_id?: string | null;
  status: string;
  triggered_by: string;
  commit_sha?: string | null;
  branch: string;
  pipelinerun_name?: string | null;
  component_name?: string | null;
  built_image_url?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  backend_image_tag?: string | null;
  frontend_image_tag?: string | null;
  build_logs: string;
  error_message?: string | null;
}

export interface DeploymentInfo {
  deployment_id: string;
  argocd_app_name: string;
  sync_status: string;
  health_status: string;
  backend_url?: string | null;
  frontend_url?: string | null;
  deployed_at?: string | null;
  last_sync_at?: string | null;
  rollback_version?: string | null;
}

export interface ForgeApplicationDetails extends ForgeApplication {
  config?: {
    name: string;
    app_type: string;
    created_by: string;
    template_name: string;
    source_repo_url: string;
    build_config: Record<string, unknown>;
    runtime_config: Record<string, unknown>;
    resource_limits: Record<string, string>;
  } | null;
  internal_endpoints: Record<string, string>;
  current_build?: BuildInfo | null;
  deployment_info?: DeploymentInfo | null;
  build_history: BuildInfo[];
  docker_images: Record<string, string>;
  resource_usage: Record<string, string>;
  last_health_check?: string | null;
  readme_content?: string | null;
  // Derived fields
  readme?: string | null;
  logs?: string[] | null;
}

// Forge entities with Etabli context
export interface ForgeWithDomain extends Forge {
  domain_id?: string | null;
  domain_name?: string | null;
}

export interface ForgeApplicationWithContext extends ForgeApplication {
  use_case_id?: string | null;
  use_case_name?: string | null;
  cadrage_id?: string | null;
  cadrage_name?: string | null;
}

// Mapping request types
export interface ForgeClientMapping {
  forge_client_id: string | null;
}

export interface ForgeMapping {
  forge_id: string | null;
}

export interface ForgeApplicationMapping {
  forge_application_id: string | null;
}

// =============================================================================
// Super-admin Forge management request types
// (Used by the /forge-dashboard CRUD tabs — mirror Forge API schemas)
// =============================================================================

export interface VectorDBConfig {
  chromadb_image?: string;
  storage_size?: string;
  pvc_config?: Record<string, unknown> | null;
  port?: number;
}

export interface CreateClientRequest {
  name: string;
  user_id?: string | null;
  quotas?: ResourceQuotas | null;
  model_embedding?: string;
  vector_dimensions?: number;
  base_port?: number;
}

export interface UpdateClientRequest {
  name?: string | null;
  quotas?: ResourceQuotas | null;
  model_embedding?: string | null;
  vector_dimensions?: number | null;
  vector_db_config?: VectorDBConfig | null;
}

export interface CreateForgeRequest {
  name: string;
  display_name: string;
  description?: string;
  system_apps?: string[];
  allowed_developers?: string[];
  resource_limits?: Record<string, string>;
  validation_strategy?: 'strict' | 'permissive';
  auto_rollback_enabled?: boolean;
  registry_storage_size?: string;
}

export interface UpdateForgeRequest {
  display_name?: string | null;
  status?: string | null;
  system_apps?: string[] | null;
  custom_apps?: string[] | null;
  allowed_developers?: string[] | null;
  resource_limits?: Record<string, string> | null;
  validation_strategy?: 'strict' | 'permissive' | null;
  auto_rollback_enabled?: boolean | null;
}

export interface CreateStandaloneAppRequest {
  client_id: string;
  forge_id: string;
  name: string;
  app_type: string;
  template_name: string;
  created_by: string;
  description?: string | null;
  build_config?: Record<string, unknown>;
  runtime_config?: Record<string, unknown>;
  resource_limits?: Record<string, string>;
}

export interface UpdateStandaloneAppRequest {
  status?: string | null;
  description?: string | null;
}

export interface CustomAppTemplateInfo {
  template_name: string;
  display_name: string;
  description: string;
  app_type: string;
  has_backend?: boolean;
  has_frontend?: boolean;
  dockerfile_paths?: Record<string, string>;
  default_ports?: Record<string, number>;
  default_env_vars?: Record<string, string>;
  default_resources?: Record<string, string>;
  version?: string;
  source_repo?: string | null;
  documentation_url?: string | null;
}

export interface AvailableTemplatesResponse {
  templates: CustomAppTemplateInfo[];
  total_count: number;
}

// =============================================================================
// Forge Pods and Logs Types
// =============================================================================

export interface ForgePod {
  name: string;
  phase: string;
  ready?: boolean;
  restarts: number;
}

export interface ForgeLogsParams {
  pod: string;
  container?: string;
  tail_lines?: number;
  previous?: boolean;
  log_level?: string;
  /** Only include logs at or after this time (ISO 8601) */
  from_time?: string;
  /** Only include logs at or before this time (ISO 8601) */
  to_time?: string;
}

export interface ForgeLogEntry {
  timestamp: string;
  pod_name: string;
  container: string;
  level?: string;
  message: string;
}

export interface ForgeLogsResponse {
  logs: ForgeLogEntry[];
  total_count: number;
  filtered_count: number;
}

export interface ForgeDetail {
  forge_id: string;
  client_id: string;
  name: string;
  display_name: string;
  status: string;
  system_apps: string[];
  custom_apps: string[];
  forge_urls: Record<string, string>;
  api_endpoints: Record<string, string>;
  forge_infra_repo?: string | null;
  argocd_pods_status?: ForgePod[];
  resource_limits: Record<string, string>;
  resource_usage?: Record<string, string> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ForgeHealth {
  forge_id: string;
  status: string;
  health_status: string;
  system_apps_health: Record<string, string>;
  custom_apps_health: Record<string, string>;
  accessible_urls: string[];
  failed_urls: string[];
  last_health_check?: string | null;
  uptime_percentage?: number | null;
  error_count: number;
  resource_usage: Record<string, string>;
  resource_limits: Record<string, string>;
}

export interface ForgeArgoCDStatus {
  sync_status?: string;
  health_status?: string;
  revision?: string;
  last_sync?: string | null;
  [key: string]: unknown;
}

export interface ForgeServiceItem {
  service_id: string;
  forge_id: string;
  service_name: string;
  service_type: string;
  template_name: string;
  display_name: string;
  status: string;
  endpoints?: Record<string, unknown> | null;
  connection_profiles?: Record<string, unknown> | null;
  secret_refs?: Record<string, unknown> | null;
  secret_values?: Record<string, string> | null;
  exposure?: Record<string, unknown> | null;
  config?: Record<string, unknown> | null;
  revealed?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

// Custom app environment variables / service bindings
export interface CustomAppServiceVariable {
  name: string;
  value: string;
}

export interface CustomAppVariablesByService {
  service: string;
  deployment_manifest_path: string;
  variables: CustomAppServiceVariable[];
}
